/**
 * Decides how a row in the "By folder" / "By URL" tables should be presented.
 *
 * - `/` → "Homepage" + raw `/` as muted secondary line.
 * - Last segment looks like a file (has an extension like `.txt`, `.xml`, `.json`)
 *   → file/document icon to visually distinguish it from real folders.
 * - Anything else → folder icon for the "folder" group, globe icon for the "url" group.
 */

export type ContentPathContext = 'folder' | 'url';

export interface ContentPathPresentation {
  label: string;
  secondaryLabel?: string;
  iconClass: string;
  title: string;
}

const FILE_EXTENSION_RE = /\.[a-z0-9]{1,8}$/i;

const isFileSegment = (path: string): boolean => {
  const parts = path.split('/').filter(Boolean);
  if (parts.length === 0) return false;
  const last = parts[parts.length - 1];
  return FILE_EXTENSION_RE.test(last);
};

export const presentContentPath = (
  path: string,
  context: ContentPathContext,
  homepageLabel: string
): ContentPathPresentation => {
  const safePath = path && path.length > 0 ? path : '/';

  if (safePath === '/') {
    return {
      label: homepageLabel,
      secondaryLabel: '/',
      iconClass: 'ico--globe',
      title: `${homepageLabel} (/)`,
    };
  }

  if (isFileSegment(safePath)) {
    return {
      label: safePath,
      iconClass: 'ico--fav-doc',
      title: safePath,
    };
  }

  return {
    label: safePath,
    iconClass: context === 'url' ? 'ico--globe' : 'ico--folder',
    title: safePath,
  };
};
